// Turns the live style into "what can I read, and what can I not" — the part of the reader
// that decides things, kept pure so it tests in node.
//
// It enumerates the STYLE, not our app models. That is the whole point. The bug this exists
// to fix happened because the ground model enumerated model types and nobody added remote
// tilesets, so an orthomosaic was silently invisible. A style walk picks up any raster layer
// anyone mounts, and anything it cannot classify becomes a loud `unsupported-source` rather
// than a silent absence.

export type UnreadableReason =
  | 'source-missing'
  | 'source-not-loaded'
  | 'no-tile-url'
  | 'placeholder-image'
  | 'unsupported-source'

export interface IUnreadableGround {
  layerId: string
  sourceId: string
  sourceType: string
  reason: UnreadableReason
}

export interface ILayerSnapshot {
  id: string
  type: string
  source?: string
  minzoom?: number
  maxzoom?: number
  visibility?: string
  rasterOpacity?: number
}

export interface ISourceSnapshot {
  type: string
  // raster
  tiles?: string[]
  tileSize?: number
  scheme?: string
  minzoom?: number
  maxzoom?: number
  // TileJSON order: west, south, east, north.
  bounds?: [number, number, number, number]
  // image
  imageWidth?: number
  imageHeight?: number
  coordinates?: Array<[number, number]>
}

export interface IRasterLayerDescriptor {
  layerId: string
  sourceId: string
  // How the pixels are reached. `image` is already decoded in the process; `tiles` needs a fetch.
  kind: 'image' | 'tiles'
  // Folded into the sample's alpha, so a half-opacity overlay composites at half strength.
  opacity: number
  tileTemplate?: string
  tileSize?: number
  scheme?: 'xyz' | 'tms'
  minzoom?: number
  maxzoom?: number
  // Absent when the source declares none — the case mapbox cannot narrow either.
  bounds?: [number, number, number, number]
  coordinates?: Array<[number, number]>
  imageWidth?: number
  imageHeight?: number
}

// The basemap-render guard swaps an oversized plan for a 1x1 transparent pixel inside the
// native WebView, because the real one OOM-kills the web process. That placeholder is not
// ground; compositing it would report the plan as covering a label it is not drawing.
const PLACEHOLDER_MAX_EDGE = 2

export function describeRasterLayers(
  layers: ILayerSnapshot[],
  sources: Record<string, ISourceSnapshot | undefined>,
  mapZoom: number,
): {
  descriptors: IRasterLayerDescriptor[]
  unreadable: IUnreadableGround[]
} {
  const descriptors: IRasterLayerDescriptor[] = []
  const unreadable: IUnreadableGround[] = []

  for (const layer of layers) {
    if (layer.type !== 'raster') continue
    // Not drawn is not ground. Mapbox is not painting it either, so there is nothing to read
    // and nothing worth reporting.
    if (layer.visibility === 'none') continue
    if (layer.rasterOpacity === 0) continue
    if (layer.minzoom != null && mapZoom < layer.minzoom) continue
    if (layer.maxzoom != null && mapZoom >= layer.maxzoom) continue

    const sourceId = layer.source ?? ''
    const source = sources[sourceId]
    if (!source) {
      unreadable.push({
        layerId: layer.id,
        sourceId,
        sourceType: 'missing',
        reason: 'source-missing',
      })
      continue
    }

    const opacity = layer.rasterOpacity ?? 1

    if (source.type === 'image') {
      const w = source.imageWidth ?? 0
      const h = source.imageHeight ?? 0
      if (!w || !h) {
        unreadable.push({
          layerId: layer.id,
          sourceId,
          sourceType: source.type,
          reason: 'source-not-loaded',
        })
        continue
      }
      if (w <= PLACEHOLDER_MAX_EDGE && h <= PLACEHOLDER_MAX_EDGE) {
        unreadable.push({
          layerId: layer.id,
          sourceId,
          sourceType: source.type,
          reason: 'placeholder-image',
        })
        continue
      }
      descriptors.push({
        layerId: layer.id,
        sourceId,
        kind: 'image',
        opacity,
        coordinates: source.coordinates,
        imageWidth: w,
        imageHeight: h,
      })
      continue
    }

    if (source.type === 'raster') {
      // Readiness is "do we have a tile template", not the source's own loaded() flag.
      // loaded() reports the TileJSON round-trip, which is still in flight long after the
      // templates are known — for a source declared with explicit tiles they are known from
      // the start. Gating on it made a live orthomosaic read as absent for seconds at a
      // time, and a label standing on it took the basemap's ink instead.
      if (!source.tiles?.length) {
        // No template yet. Retried when the source settles.
        unreadable.push({
          layerId: layer.id,
          sourceId,
          sourceType: source.type,
          reason: 'source-not-loaded',
        })
        continue
      }
      const template = source.tiles[0]
      descriptors.push({
        layerId: layer.id,
        sourceId,
        kind: 'tiles',
        opacity,
        tileTemplate: template,
        tileSize: source.tileSize ?? 512,
        scheme: source.scheme === 'tms' ? 'tms' : 'xyz',
        minzoom: source.minzoom ?? 0,
        maxzoom: source.maxzoom ?? 22,
        bounds: source.bounds,
      })
      continue
    }

    // A raster layer over a source kind we have no reader for. This is the branch that keeps
    // the enumeration hazard from recurring: it is reported, once, rather than skipped.
    unreadable.push({
      layerId: layer.id,
      sourceId,
      sourceType: source.type,
      reason: 'unsupported-source',
    })
  }

  return { descriptors, unreadable }
}
